import type { PackageSearchValues } from './packageSearchQuery';
import { placeIdsParam } from './packageSearchQuery';

export type PackageSearchField = 'origin' | 'destination' | 'dates' | 'passengers';

export type PackageSearchErrors = Partial<Record<PackageSearchField, string>>;

const MESSAGES: Record<PackageSearchField, string> = {
  origin: 'Ingresá tu origen',
  destination: 'Ingresá tu destino',
  dates: 'Elegí el mes de salida',
  passengers: 'Elegí la cantidad de pasajeros',
};

export const SAME_ORIGIN_DESTINATION_MESSAGE =
  'No podés realizar una búsqueda con el mismo origen y destino.';

const FIELD_LABELS: Record<PackageSearchField, string> = {
  origin: 'origen',
  destination: 'destino',
  dates: 'fechas',
  passengers: 'turistas',
};

function sameIds(values: PackageSearchValues): boolean {
  const origin = placeIdsParam(values.origin).split(',').filter(Boolean).sort().join(',');
  const destination = placeIdsParam(values.destination).split(',').filter(Boolean).sort().join(',');

  return Boolean(origin && destination && origin === destination);
}

/**
 * Typed port of `validatePackagesSearchSubmit` — same rules the legacy form
 * enforces, returned per-field so the hero card can mark every missing input at
 * once instead of only the first one.
 */
export function validatePackageSearch(values: PackageSearchValues): PackageSearchErrors {
  const errors: PackageSearchErrors = {};

  if (!values.origin?.name.trim() || !placeIdsParam(values.origin)) {
    errors.origin = MESSAGES.origin;
  }
  if (!values.destination?.name.trim() || !placeIdsParam(values.destination)) {
    errors.destination = MESSAGES.destination;
  }
  if (!errors.origin && !errors.destination && sameIds(values)) {
    errors.destination = SAME_ORIGIN_DESTINATION_MESSAGE;
  }
  if (!values.month || !values.year) {
    errors.dates = MESSAGES.dates;
  }
  if (!values.adults || values.adults < 1) {
    errors.passengers = MESSAGES.passengers;
  }

  return errors;
}

/** Single sentence summarizing the validation result, for the form-level message. */
export function summarizeMissingFields(errors: PackageSearchErrors): string {
  if (errors.destination === SAME_ORIGIN_DESTINATION_MESSAGE) {
    return SAME_ORIGIN_DESTINATION_MESSAGE;
  }

  const labels = (Object.keys(FIELD_LABELS) as PackageSearchField[])
    .filter((field) => Boolean(errors[field]))
    .map((field) => FIELD_LABELS[field]);

  if (labels.length === 0) return '';
  if (labels.length === 1) return `Completá ${labels[0]} para continuar.`;

  const last = labels[labels.length - 1];
  return `Completá ${labels.slice(0, -1).join(', ')} y ${last} para continuar.`;
}
